import { useEffect, useState, type ReactNode } from 'react';
import {
  CircleDollarSign,
  Gavel,
  LayoutDashboard,
  ListChecks,
  LogIn,
  LogOut,
  Megaphone,
  ReceiptText,
  Settings2,
  Share2,
  ShieldCheck,
  ShoppingBag,
  Ticket,
  UserCircle,
  Users
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import { isNative, shareApp } from '../native';
import { formatMoney, roleLabel } from '../utils';
import { AccessBanner } from './AccessBanner';
import { NetworkStatusBanner } from './NetworkStatusBanner';
import { NotificationBell } from './NotificationBell';

export type ViewKey = 'dashboard' | 'center' | 'commissions' | 'market' | 'auctions' | 'notices' | 'members' | 'settings' | 'redeem' | 'audit' | 'profile';

const desktopNav: Array<{ key: ViewKey; label: string; icon: typeof LayoutDashboard; group?: 'staff' }> = [
  { key: 'dashboard', label: '总览', icon: LayoutDashboard },
  { key: 'commissions', label: '委托大厅', icon: ListChecks },
  { key: 'center', label: '交易中心', icon: ShoppingBag },
  { key: 'auctions', label: '法拍场', icon: Gavel },
  { key: 'notices', label: '公告', icon: Megaphone },
  { key: 'members', label: '成员管理', icon: Users, group: 'staff' },
  { key: 'redeem', label: '兑换码', icon: Ticket, group: 'staff' },
  { key: 'settings', label: '系统设置', icon: Settings2, group: 'staff' },
  { key: 'audit', label: '审计日志', icon: ReceiptText, group: 'staff' },
  { key: 'profile', label: '我的账户', icon: UserCircle }
];

const mobileNav: Array<{ key: ViewKey; label: string; icon: typeof LayoutDashboard }> = [
  { key: 'dashboard', label: '总览', icon: LayoutDashboard },
  { key: 'commissions', label: '委托', icon: ListChecks },
  { key: 'center', label: '市场', icon: ShoppingBag },
  { key: 'auctions', label: '拍卖', icon: Gavel },
  { key: 'profile', label: '我的', icon: UserCircle }
];

export function AppShell({ view, onNavigate, onOpenAuth, children }: { view: ViewKey; onNavigate: (view: ViewKey) => void; onOpenAuth: () => void; children: ReactNode }) {
  const { user, settings, permissions, logout, refresh } = useAuth();
  const { confirm, toast } = useUi();
  const [accountOpen, setAccountOpen] = useState(false);
  const visibleDesktopNav = desktopNav.filter(item => !item.group || permissions.isStaff);
  const current = desktopNav.find(item => item.key === view);
  const appName = isNative ? '服务器小助手' : settings.siteName;

  useEffect(() => setAccountOpen(false), [view]);

  const doLogout = async () => {
    const approved = await confirm({ title: '退出登录', body: '确认退出当前账号？', danger: true, confirmText: '退出登录' });
    if (approved) await logout();
  };

  const doShare = async () => {
    setAccountOpen(false);
    const result = await shareApp();
    if (result === 'copied') toast('App 链接已复制', 'success');
    if (result === 'failed') toast('分享失败，请稍后重试', 'error');
  };

  return (
    <div className="app-shell prism-console">
      <aside className="sidebar prism-sidebar">
        <div className="brand-block prism-brand">
          <button className="brand-home" onClick={() => onNavigate('dashboard')}>
            <span className="brand-mark prism-brand-mark"><ShieldCheck size={21} /></span>
            <span><strong>{appName}</strong><small>{isNative ? 'SERVER ASSISTANT' : 'SERVER DISPATCH CENTER'}</small></span>
          </button>
        </div>
        <nav className="side-nav prism-nav">
          <span className="prism-nav-label">工作台</span>
          {visibleDesktopNav.filter(item => item.group !== 'staff').map(item => {
            const Icon = item.icon;
            return <button key={item.key} className={'side-nav-item prism-nav-item ' + (view === item.key ? 'active' : '')} onClick={() => onNavigate(item.key)}><Icon size={18} /><span>{item.label}</span></button>;
          })}
          {permissions.isStaff && <span className="prism-nav-label prism-nav-gap">管理</span>}
          {visibleDesktopNav.filter(item => item.group === 'staff').map(item => {
            const Icon = item.icon;
            return <button key={item.key} className={'side-nav-item prism-nav-item ' + (view === item.key ? 'active' : '')} onClick={() => onNavigate(item.key)}><Icon size={18} /><span>{item.label}</span></button>;
          })}
        </nav>
        <div className="sidebar-account prism-sidebar-user">
          {user ? (
            <>
              <span className="account-avatar prism-avatar">{user.username.slice(0, 1).toUpperCase()}</span>
              <div className="account-copy"><strong>{user.displayName}</strong><small>{roleLabel[user.role]} · {formatMoney(user.balanceCents, settings.currency)}</small></div>
            </>
          ) : <button className="button primary wide" onClick={onOpenAuth}><LogIn size={17} /> 登录 / 注册</button>}
        </div>
      </aside>

      <div className="workspace prism-workspace">
        <AccessBanner
          user={user}
          isStaff={permissions.isStaff}
          onLogin={onOpenAuth}
          onGoProfile={() => onNavigate('profile')}
          onRefresh={refresh}
        />
        <NetworkStatusBanner />
        <header className="mobile-header prism-topbar">
          <button className="mobile-brand" onClick={() => onNavigate('dashboard')}>
            <span className="brand-mark prism-brand-mark"><ShieldCheck size={19} /></span>
            <strong>{appName}</strong>
          </button>
          <div className="prism-topbar-title"><span>{appName}</span><strong>{current?.label || '工作台'}</strong></div>
          <div className="prism-topbar-actions">
            <span className="prism-status"><i />在线</span>
            {user && <NotificationBell onNavigate={onNavigate} />}
            {user ? (
              <div className="account-menu-wrap">
                <button className="topbar-account" onClick={() => setAccountOpen(value => !value)}>
                  <span className="account-avatar prism-avatar">{user.username.slice(0, 1).toUpperCase()}</span>
                  <span className="mobile-balance"><CircleDollarSign size={14} />{formatMoney(user.balanceCents, settings.currency)}</span>
                  <span className="desktop-account-copy"><strong>{user.displayName}</strong><small>{roleLabel[user.role]}</small></span>
                </button>
                {accountOpen && (
                  <div className="account-popover">
                    <div><strong>{user.displayName}</strong><small>@{user.username} · {roleLabel[user.role]}</small></div>
                    <button onClick={() => onNavigate('profile')}><UserCircle size={16} />我的账户</button>
                    <button onClick={() => void doShare()}><Share2 size={16} />分享服务器小助手</button>
                    <button className="danger-text" onClick={() => void doLogout()}><LogOut size={16} />退出登录</button>
                  </div>
                )}
              </div>
            ) : <button className="button primary" onClick={onOpenAuth}><LogIn size={16} /> 登录</button>}
          </div>
        </header>
        <main className="content prism-content"><div className="page-enter">{children}</div></main>
      </div>

      <nav className="mobile-tabbar">
        {mobileNav.map(item => {
          const Icon = item.icon;
          const active = view === item.key || (item.key === 'profile' && ['members', 'redeem', 'settings', 'audit'].includes(view));
          return <button key={item.key} className={active ? 'active' : ''} onClick={() => onNavigate(item.key)}><Icon size={20} /><span>{item.label}</span></button>;
        })}
      </nav>
    </div>
  );
}
